import type { ReactNode } from 'react';

export interface ToolbarToggleOption<T extends string | number> {
  value: T;
  label: ReactNode;
  /** The button's accessible name, when its label alone would not say enough. */
  ariaLabel?: string;
  title?: string;
  testId?: string;
}

interface ToolbarToggleGroupProps<T extends string | number> {
  options: readonly ToolbarToggleOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Names the group for a screen reader. */
  ariaLabel: string;
  disabled?: boolean;
  'data-testid'?: string;
}

/**
 * A few joined buttons, one pressed: the floor-plan toolbar's compact way of
 * picking one of a handful of settings (units, grid size, canvas theme).
 */
export function ToolbarToggleGroup<T extends string | number>({
  options,
  value,
  onChange,
  ariaLabel,
  disabled,
  'data-testid': testId,
}: ToolbarToggleGroupProps<T>) {
  return (
    <div
      className="flex items-center rounded border border-gray-200 overflow-hidden"
      role="group"
      aria-label={ariaLabel}
      data-testid={testId}
    >
      {options.map((option) => {
        const on = option.value === value;
        return (
          <button
            key={String(option.value)}
            type="button"
            onClick={() => onChange(option.value)}
            disabled={disabled}
            aria-pressed={on}
            aria-label={option.ariaLabel}
            title={option.title}
            className={`px-1.5 py-1 disabled:opacity-50 ${
              on ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'
            }`}
            data-testid={option.testId}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
